import {
    type DependencyList,
    type ReactNode,
    useCallback,
    useContext,
    useId,
    useMemo,
} from "react";
import { flexRender, type RowData } from "@tanstack/react-table";
import { numberFormatter } from "@common/helpers/number";
import { CellControlsTabbableContext } from "../cells/cellControls";
import { CellVariantContext } from "../cells/cellVariant";
import { isShownOnSummaryRow, SummaryRowContext } from "../cells/summaryRow";
import type {
    DataGridCell,
    DataGridColumnKind,
    DataGridPin,
    DataGridResolvedColumnMeta,
} from "../columns/types";
import { CellEditor } from "../editing/CellEditor";
import type { EditContext } from "../editing/editActions";
import {
    ListCellCommitContext,
    ListCellContext,
} from "../editing/list/listCellContext";
import { getListCellState } from "../editing/list/listCellState";
import { closeListEditorAndCommit } from "../editing/list/listEditActions";
import type { GridController } from "../focus/gridController";
import type {
    DataGridActiveEditor,
    DataGridListEditingOptions,
    DataGridTableMeta,
} from "../types";
import { getRovingTabIndex } from "../focus/navigation";
import { getColumnStyle } from "../layout/columnVars";
import type { CellEditState } from "./cellEditState";
import { InvalidCellTooltip } from "./InvalidCellTooltip";
import styles from "./Grid.module.scss";

interface CellProps<TData extends RowData> extends CellEditState<TData> {
    cell: DataGridCell<TData>;
    meta: DataGridResolvedColumnMeta;
    pin: DataGridPin | false;
    pinEdge: DataGridPin | undefined;
    ariaColIndex: number;
    /** Cell navigation: the cell is in the range (or is the active cell); `undefined` in row mode. */
    isInRange: boolean | undefined;
    /** Cell navigation or a list editing stop: the cell takes DOM focus and shows the active ring. */
    controller: GridController | null;
    isActive: boolean;
    /**
     * Row state renderers draw (expanded); content redraws only when it or
     * the cell changes. Not selection: select-all would redraw every cell,
     * so the checkbox subscribes to it (`SelectCell`).
     */
    rowStateKey: string;
    /** The column's variant (`variants`); content redraws when it changes. */
    variant: string | undefined;
    /** A `parentRows: "summary"` parent row: blank except the columns it summarizes. */
    isSummaryRow: boolean;
    /** Table meta; content redraws when its identity changes. */
    tableMeta: DataGridTableMeta<TData> | undefined;
    /** List tables only; stable for the table's lifetime. */
    listEditContext: EditContext<TData> | null;
    /** This render's list editing rules; access redraws when their identity changes. */
    listEditing: DataGridListEditingOptions<TData> | undefined;
    /** The row's open editor when it is this cell's, else `null`. */
    activeEditor: DataGridActiveEditor | null;
}

/** Display columns keep rendering on group rows (checkbox, chevron, number). */
const GROUP_ROW_KINDS: ReadonlySet<DataGridColumnKind> = new Set([
    "select",
    "expand",
    "rowNumber",
]);

const renderValue = <TData extends RowData>(cell: DataGridCell<TData>) =>
    flexRender(cell.column.columnDef.cell, cell.getContext());

/**
 * Grouping modes. A group row shows its grouping value with the leaf count
 * and its columns' aggregates (through the column's own renderer, so money
 * stays formatted); its other cells are blank, as are a grouped column's
 * repeated values in leaf rows. A summary row shows only `isShownOnSummaryRow`
 * columns.
 */
const renderContent = <TData extends RowData>({
    cell,
    meta,
    isSummaryRow,
}: {
    cell: DataGridCell<TData>;
    meta: DataGridResolvedColumnMeta;
    isSummaryRow: boolean;
}): ReactNode => {
    if (cell.getIsGrouped()) {
        return (
            <span className={styles.groupValue}>
                {renderValue(cell)}
                <span className={styles.groupCount}>
                    {numberFormatter.format(cell.row.getLeafRows().length)}
                </span>
            </span>
        );
    }
    if (cell.getIsPlaceholder()) {
        return null;
    }
    if (cell.row.getIsGrouped()) {
        const isShown =
            GROUP_ROW_KINDS.has(meta.kind) ||
            (cell.getIsAggregated() && meta.aggregate !== undefined);
        return isShown ? renderValue(cell) : null;
    }
    if (isSummaryRow && !isShownOnSummaryRow(meta)) {
        return null;
    }
    return renderValue(cell);
};

/**
 * A cell's content with its summary-row and variant contexts. TanStack keeps
 * `cell` the same object while its row's state and the table meta change, so
 * those arrive as `_redrawKeys`: unread here, but a call input, so React
 * Compiler and the caller's `useMemo` redraw the content when one changes.
 */
const renderCellContent = <TData extends RowData>(
    {
        cell,
        meta,
        variant,
        isSummaryRow,
    }: {
        cell: DataGridCell<TData>;
        meta: DataGridResolvedColumnMeta;
        variant: CellProps<TData>["variant"];
        isSummaryRow: boolean;
    },
    _redrawKeys: DependencyList,
) => (
    <div className={styles.cellContent}>
        <SummaryRowContext.Provider value={isSummaryRow}>
            <CellVariantContext.Provider value={variant}>
                {renderContent({ cell, meta, isSummaryRow })}
            </CellVariantContext.Provider>
        </SummaryRowContext.Provider>
    </div>
);

/**
 * Grid cell wrapper: owns width, padding, alignment, truncation, pinning, and
 * (in cell navigation and list editing) the roving tabindex, the active ring,
 * and whether the cell's controls are in the Tab order. List editing keeps
 * the focused row's controls tabbable from every cell. Renderers draw only
 * content.
 */
export const Cell = <TData extends RowData>({
    cell,
    meta,
    pin,
    pinEdge,
    ariaColIndex,
    isInRange,
    controller,
    isActive,
    rowStateKey,
    variant,
    isSummaryRow,
    tableMeta,
    isHighlighted,
    invalidMessage,
    editing,
    listEditContext,
    listEditing,
    activeEditor,
}: Readonly<CellProps<TData>>) => {
    const rowId = cell.row.id;
    const columnId = cell.column.id;
    const readOnlyReasonId = useId();
    const isRowTabbable = useContext(CellControlsTabbableContext);
    const cellRef = useCallback(
        (element: HTMLDivElement | null) => {
            if (!controller) {
                return undefined;
            }
            controller.registerCell({ rowId, columnId }, element);
            return () => controller.registerCell({ rowId, columnId }, null);
        },
        [controller, rowId, columnId],
    );
    // Renderers redraw with their row's state, variant, and table meta, not with focus or range moves.
    const content = useMemo(
        () =>
            renderCellContent({ cell, meta, variant, isSummaryRow }, [
                rowStateKey,
                tableMeta,
            ]),
        [cell, meta, rowStateKey, variant, isSummaryRow, tableMeta],
    );
    const listCellState = useMemo(
        () =>
            getListCellState({
                cell,
                listEditContext,
                listEditing,
                editor: activeEditor,
            }),
        [cell, listEditContext, listEditing, activeEditor],
    );
    const commitFromCell = useMemo(
        () =>
            listEditContext === null
                ? null
                : (commit: () => void) =>
                      closeListEditorAndCommit(listEditContext, {
                          address: { rowId, columnId },
                          commit,
                      }),
        [listEditContext, rowId, columnId],
    );
    const isListEditable = listCellState?.access === "editable";
    const hasListPopup = isListEditable && meta.editorPopup !== undefined;
    const isEditingInline =
        isListEditable && !hasListPopup && listCellState.isOpen;
    const listReadOnlyReason =
        listCellState?.access === "readOnly"
            ? listCellState.readOnlyReason
            : undefined;

    return (
        <div
            ref={cellRef}
            role="gridcell"
            aria-colindex={ariaColIndex}
            aria-selected={isInRange}
            tabIndex={getRovingTabIndex({
                isFocusTarget: controller !== null,
                isFocused: isActive,
            })}
            className={styles.cell}
            style={getColumnStyle(columnId, pin)}
            data-column-id={columnId}
            data-kind={meta.kind}
            data-align={meta.align}
            data-overflow={meta.overflow}
            data-layout={meta.layout === "fill" ? "fill" : undefined}
            data-list-editing={isEditingInline ? "inline" : undefined}
            data-pin={pin || undefined}
            data-pin-edge={pinEdge}
            data-active={isActive || undefined}
            data-highlighted={isHighlighted || undefined}
            data-invalid={invalidMessage !== undefined || undefined}
            aria-invalid={invalidMessage !== undefined || undefined}
            aria-haspopup={hasListPopup ? meta.editorPopup : undefined}
            aria-expanded={hasListPopup ? listCellState?.isOpen : undefined}
            aria-readonly={listCellState?.access === "readOnly" || undefined}
            aria-describedby={
                listReadOnlyReason === undefined ? undefined : readOnlyReasonId
            }
        >
            <ListCellCommitContext.Provider value={commitFromCell}>
                <ListCellContext.Provider value={listCellState}>
                    {controller ? (
                        <CellControlsTabbableContext.Provider
                            value={isActive || isRowTabbable}
                        >
                            {content}
                        </CellControlsTabbableContext.Provider>
                    ) : (
                        content
                    )}
                </ListCellContext.Provider>
            </ListCellCommitContext.Provider>
            {listReadOnlyReason === undefined ? null : (
                <span id={readOnlyReasonId} className={styles.cellDescription}>
                    {listReadOnlyReason}
                </span>
            )}
            {invalidMessage !== undefined && editing === null ? (
                <InvalidCellTooltip
                    message={invalidMessage}
                    isActive={isActive}
                />
            ) : null}
            {editing ? (
                <CellEditor
                    cell={cell}
                    editor={editing.editor}
                    seed={editing.seed}
                    header={meta.ariaLabel ?? meta.headerLabel}
                    context={editing.context}
                />
            ) : null}
        </div>
    );
};
